import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Minus, MessageCircle, Phone, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useEnquiryCart } from "@/contexts/EnquiryCartContext";
import { useSettings } from "@/contexts/SettingsContext";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [imgIdx, setImgIdx] = useState(0);
    const [qty, setQty] = useState(1);
    const { addItem, setOpen } = useEnquiryCart();
    const { settings } = useSettings();

    useEffect(() => {
        setImgIdx(0);
        api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => navigate("/catalog"));
        window.scrollTo(0, 0);
    }, [id, navigate]);

    useEffect(() => {
        if (!product) return;
        api.get("/products", { params: { category: product.category } }).then((r) => {
            setRelated(r.data.filter((p) => p.id !== product.id).slice(0, 4));
        });
    }, [product]);

    if (!product) return <div className="max-w-[1400px] mx-auto px-6 py-32 text-center text-muted-ink">Loading…</div>;

    const onAdd = () => { addItem(product, qty); toast.success("Added to enquiry", { description: `${product.name} × ${qty}` }); };
    const onAddAndOpen = () => { addItem(product, qty); setOpen(true); };
    const onWhatsAppDirect = () => {
        const msg = `Hello Gold by SRN — I'd like to enquire about: ${product.name} (SKU: ${product.sku}). Qty: ${qty}.`;
        window.open(buildWhatsAppLink(settings.whatsapp_number, msg), "_blank");
    };

    return (
        <div className="bg-paper">
            <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-8">
                <Link to="/catalog" data-testid="pd-back" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-muted-ink hover:text-bronze-deep">
                    <ArrowLeft size={12} /> Back to catalog
                </Link>

                <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
                    {/* IMAGE STACK */}
                    <div className="lg:col-span-7">
                        <div className="aspect-[4/5] bg-ivory overflow-hidden">
                            <img src={product.images?.[imgIdx]} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                        {product.images?.length > 1 && (
                            <div className="mt-3 grid grid-cols-5 gap-2">
                                {product.images.map((src, i) => (
                                    <button
                                        key={src || `thumb-${i}`}
                                        onClick={() => setImgIdx(i)}
                                        data-testid={`pd-thumb-${i}`}
                                        className={`aspect-square overflow-hidden border ${imgIdx === i ? 'border-bronze-deep' : 'border-soft'}`}
                                    >
                                        <img src={src} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* INFO */}
                    <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
                        <p className="overline">{product.category}</p>
                        <h1 className="font-serif text-3xl sm:text-4xl text-ink mt-3 leading-tight">{product.name}</h1>
                        <div className="divider-dots my-6" />
                        <dl className="grid grid-cols-2 gap-y-3 text-sm">
                            <dt className="text-muted-ink">SKU</dt><dd className="text-ink" data-testid="pd-sku">{product.sku}</dd>
                            <dt className="text-muted-ink">Purity</dt><dd className="text-ink" data-testid="pd-purity">{product.purity}</dd>
                            <dt className="text-muted-ink">Approx Weight</dt><dd className="text-ink" data-testid="pd-weight">{product.weight_grams ? `${product.weight_grams} g` : 'On request'}</dd>
                            <dt className="text-muted-ink">Availability</dt><dd className="text-bronze-deep">Made to order</dd>
                        </dl>

                        <p className="mt-6 text-charcoal leading-relaxed text-sm">{product.description}</p>

                        <div className="mt-8 p-4 border border-bronze/30 bg-ivory">
                            <p className="overline">Trade Note</p>
                            <p className="text-xs text-charcoal mt-2 leading-relaxed">
                                No prices are displayed online. Rates are quoted live, based on prevailing gold market, design complexity & quantity. Add this piece to your enquiry — we'll share wholesale pricing on WhatsApp.
                            </p>
                        </div>

                        <div className="mt-8 flex items-center gap-4">
                            <div className="flex items-center border border-ink/70 h-12">
                                <button data-testid="pd-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 text-ink"><Minus size={14} /></button>
                                <span className="px-4 text-sm" data-testid="pd-qty">{qty}</span>
                                <button data-testid="pd-qty-inc" onClick={() => setQty((q) => q + 1)} className="px-3 text-ink"><Plus size={14} /></button>
                            </div>
                            <button
                                onClick={onAdd}
                                data-testid="pd-add-enquiry"
                                className="flex-1 h-12 bg-ink text-paper hover:bg-bronze-deep uppercase text-[11px] tracking-[0.22em] flex items-center justify-center gap-2"
                            >
                                <Plus size={13} /> Add to Enquiry
                            </button>
                        </div>

                        <button
                            onClick={onAddAndOpen}
                            data-testid="pd-view-enquiry"
                            className="mt-3 w-full h-12 border border-ink text-ink hover:bg-ink hover:text-paper uppercase text-[11px] tracking-[0.22em]"
                        >
                            Add &amp; View Enquiry List
                        </button>

                        <button
                            onClick={onWhatsAppDirect}
                            data-testid="pd-whatsapp-direct"
                            className="mt-3 w-full h-12 bg-[#25D366] text-white hover:bg-[#1DA851] uppercase text-[11px] tracking-[0.22em] flex items-center justify-center gap-2"
                        >
                            <MessageCircle size={14} /> Ask about this on WhatsApp
                        </button>

                        <div className="mt-8 grid grid-cols-2 gap-3 text-[11px]">
                            <a href={`tel:${settings.phone_display.replace(/\s/g, '')}`} className="flex items-center justify-center gap-2 h-11 border border-soft text-charcoal hover:border-bronze uppercase tracking-[0.2em]" data-testid="pd-call">
                                <Phone size={12} /> Call us
                            </a>
                            <a href={`mailto:${settings.email}`} className="flex items-center justify-center gap-2 h-11 border border-soft text-charcoal hover:border-bronze uppercase tracking-[0.2em]" data-testid="pd-email">
                                <Mail size={12} /> Email
                            </a>
                        </div>
                    </div>
                </div>

                {related.length > 0 && (
                    <div className="mt-24">
                        <div className="text-center mb-10">
                            <p className="overline">You might also like</p>
                            <h2 className="font-serif text-3xl text-ink mt-3">More in {product.category}</h2>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                            {related.map((p) => <ProductCard key={p.id} product={p} />)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
